import React from 'react';
import LegalDocumentLayout, { LegalSection } from './LegalDocumentLayout';

const LAST_UPDATED = 'April 11, 2026';

/**
 * Privacy practices aligned with the RINK web app + API: MongoDB jobs/results,
 * JWT auth, PayPal subscriptions, contact form, referral codes, admin features.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalDocumentLayout title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <p className="text-gray-600 mb-8">
        RINK (“we,” “us,” or “our”) provides the Rink Data platform (website, dashboard, and APIs) that
        lets customers configure and run web-scraping jobs and manage results. This Privacy Policy
        describes how we collect, use, and share personal information in connection with those
        services.
      </p>

      <LegalSection id="collect" title="1. Information we collect">
        <p>
          <strong className="text-gray-900">Account and profile.</strong> When you register or maintain
          an account, we process information you provide, such as username, email address, full name,
          phone number, and mailing or location fields (for example country, city, state, zip, and
          address) used for account setup, billing, or support.
        </p>
        <p>
          <strong className="text-gray-900">Authentication.</strong> Passwords are stored using strong
          one-way hashing (we do not store plaintext passwords). Sessions may be secured with signed
          tokens (for example JWTs) presented with API requests after you sign in.
        </p>
        <p>
          <strong className="text-gray-900">Scraping jobs and results.</strong> We store the scraping
          jobs you create and parameters you submit (such as search terms, locations, and scraper
          settings), job status, timestamps, and result rows produced by or associated with your jobs.
          This data is tied to your account and is used only to operate the service you requested.
        </p>
        <p>
          <strong className="text-gray-900">Contact and support.</strong> If you use our contact form or
          email us, we collect the information you send (such as name, email, and message content) to
          respond and improve support.
        </p>
        <p>
          <strong className="text-gray-900">Referral codes.</strong> If you arrive with a referral
          parameter in the URL, we may store that code locally in your browser to associate your
          registration with a referral program, where applicable.
        </p>
        <p>
          <strong className="text-gray-900">Payments.</strong> Subscription billing is processed through
          PayPal. We receive and store information necessary to link your account to your subscription
          and payment status (for example PayPal subscription identifiers and related billing events).
          PayPal processes card and wallet details according to its own privacy policy.
        </p>
        <p>
          <strong className="text-gray-900">Browser storage.</strong> The dashboard may store a session
          token and limited cached account information in your browser (for example{' '}
          <code className="text-sm bg-gray-100 px-1 rounded">localStorage</code>) so you stay signed in
          and to reduce unnecessary API calls. Administrative “impersonation” or similar support
          features, when used, may store additional tokens only as required for those secure workflows.
        </p>
        <p>
          <strong className="text-gray-900">Technical data.</strong> Like most hosted applications, our
          servers and infrastructure may log IP addresses, request metadata, and error logs for security,
          debugging, and reliability.
        </p>
      </LegalSection>

      <LegalSection id="use" title="2. How we use information">
        <ul className="list-disc pl-6 space-y-2">
          <li>To create and manage your account and authenticate you.</li>
          <li>To run and display scraping jobs, store results, and provide exports (for example spreadsheets).</li>
          <li>To process subscriptions and payments through PayPal and maintain billing records.</li>
          <li>To communicate with you about the service, security, or policy updates.</li>
          <li>To respond to contact submissions and provide customer support.</li>
          <li>To protect the platform, detect abuse, and comply with legal obligations.</li>
        </ul>
      </LegalSection>

      <LegalSection id="sharing" title="3. How we share information">
        <p>
          We do not sell your personal information. We share data only as needed to operate the
          service:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong className="text-gray-900">Payment processors:</strong> PayPal receives payment-related
            data needed to complete transactions you authorize.
          </li>
          <li>
            <strong className="text-gray-900">Infrastructure providers:</strong> Hosting, database, and
            related vendors process data on our behalf under appropriate safeguards.
          </li>
          <li>
            <strong className="text-gray-900">Legal:</strong> We may disclose information if required by
            law, court order, or to protect our rights, users, or the public.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="retention" title="4. Retention">
        <p>
          We retain account and job data for as long as your account is active and as needed to provide
          the service, comply with law, resolve disputes, and enforce our agreements. Some records (such
          as billing history) may be kept longer where required for accounting or legal reasons. Jobs or
          results may be removed or soft-deleted according to product behavior and your actions in the
          application.
        </p>
      </LegalSection>

      <LegalSection id="security" title="5. Security">
        <p>
          We use industry-standard measures appropriate to the nature of the service, including encrypted
          transport (HTTPS), hashed passwords, and access controls on our systems. No method of
          transmission or storage is 100% secure; you are responsible for keeping your password
          confidential.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="6. Your choices and rights">
        <p>
          Depending on your location, you may have rights to access, correct, delete, or export personal
          information, or to object to certain processing. To exercise these rights, contact us using
          the information below. We may need to verify your identity before fulfilling a request.
        </p>
      </LegalSection>

      <LegalSection id="children" title="7. Children’s privacy">
        <p>
          The service is not directed to children under 13 (or the minimum age in your jurisdiction),
          and we do not knowingly collect personal information from children. If you believe we have done
          so, contact us and we will take appropriate steps.
        </p>
      </LegalSection>

      <LegalSection id="international" title="8. International users">
        <p>
          If you access the service from outside the United States, your information may be processed in
          the United States or other countries where we or our vendors operate, which may have different
          data protection rules than your country.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="9. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. We will post the revised policy on this
          page and update the “Last updated” date. Continued use of the service after changes constitutes
          acceptance of the updated policy where permitted by law.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="10. Contact us">
        <p>
          Questions about this Privacy Policy or our data practices:
        </p>
        <ul className="list-none space-y-1 mt-2">
          <li>
            Email:{' '}
            <a href="mailto:support@rinkdata.com" className="text-blue-600 hover:underline">
            support@rinkdata.com
            </a>{' '}
            or{' '}
            <a href="mailto:tom@cscbeyond.com" className="text-blue-600 hover:underline">
              tom@cscbeyond.com
            </a>
          </li>
          <li>Phone: (919) 679-1559</li>
          <li>Location: Raleigh, NC, United States</li>
        </ul>
      </LegalSection>
    </LegalDocumentLayout>
  );
}
